import { waitForPendingWrites } from './db';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const openedOn = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Bangkok', year: 'numeric', month: '2-digit', day: '2-digit'
}).format(new Date());
let checking = false;
let reloading = false;

type VersionPayload = { buildId?: unknown };

async function fetchLatestBuildId(): Promise<string | null> {
  const response = await fetch(`/version.json?t=${Date.now()}`, {
    cache: 'no-store',
    headers: { Accept: 'application/json' }
  });
  if (!response.ok) return null;
  const payload = await response.json() as VersionPayload;
  return typeof payload.buildId === 'string' ? payload.buildId : null;
}

async function activateUpdate(registration: ServiceWorkerRegistration): Promise<void> {
  await waitForPendingWrites();

  const changed = new Promise<void>((resolve) => {
    navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), { once: true });
  });

  registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
  await registration.update();

  const installing = registration.installing;
  if (installing) {
    await new Promise<void>((resolve) => {
      if (installing.state === 'installed' || installing.state === 'activated') return resolve();
      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed' || installing.state === 'activated' || installing.state === 'redundant') resolve();
      });
    });
  }
  registration.waiting?.postMessage({ type: 'SKIP_WAITING' });

  await Promise.race([changed, new Promise<void>((resolve) => window.setTimeout(resolve, 4000))]);
  window.location.reload();
}

async function checkForUpdate(registration: ServiceWorkerRegistration): Promise<void> {
  if (checking || reloading) return;
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok', year: 'numeric', month: '2-digit', day: '2-digit'
  }).format(new Date());
  if (today !== openedOn) {
    reloading = true;
    await waitForPendingWrites();
    window.location.reload();
    return;
  }
  if (!navigator.onLine) return;
  checking = true;
  try {
    const latestBuildId = await fetchLatestBuildId();
    if (latestBuildId && latestBuildId !== __APP_BUILD_ID__) {
      reloading = true;
      await activateUpdate(registration);
    }
  } catch {
    // Offline, captive portals, and temporary server failures are intentionally silent.
  } finally {
    checking = false;
  }
}

export async function startAppUpdateChecks(): Promise<void> {
  if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return;

  const registration = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
  void checkForUpdate(registration);

  window.addEventListener('online', () => void checkForUpdate(registration));
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) void checkForUpdate(registration);
  });
  window.setInterval(() => void checkForUpdate(registration), CHECK_INTERVAL_MS);
}
